"use client";

import { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import JumiaIcon from "../../../assets/JumiaLogo.png";
import Axamansard from "../../../assets/AXAMansardLogo.png";
import Gt from "../../../assets/GTBank.png";
import Nibss from "../../../assets/Nibbs.png";
import { useNavigate } from "react-router-dom";

interface CustomerLogo {
  src: string;
  alt: string;
  size?: "normal" | "large";
}

const Customers = () => {
  const controls = useAnimation();
  const hasAnimated = useRef(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: "0px 0px -100px 0px",
  });

  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/register");
  };

  useEffect(() => {
    if (inView && !hasAnimated.current) {
      hasAnimated.current = true;
      controls.start("visible");
    }
  }, [controls, inView]);

  useEffect(() => {
    if (hasAnimated.current) return;

    const checkInitialVisibility = setTimeout(() => {
      const element = document.querySelector("[data-inview-ref]");
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && !hasAnimated.current) {
          hasAnimated.current = true;
          controls.start("visible");
        }
      }
    }, 100);

    return () => clearTimeout(checkInitialVisibility);
  }, []); // Empty dependency array to run only once

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 150,
        duration: 0.3,
      },
    },
  };

  const customerLogos: (CustomerLogo & { size?: "normal" | "large" })[] = [
    { src: JumiaIcon, alt: "Jumia", size: "normal" },
    { src: Axamansard, alt: "AXA Mansard", size: "normal" },
    { src: Gt, alt: "GT Bank", size: "large" },
    { src: Nibss, alt: "NIBSS", size: "large" },
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          ref={(node: HTMLElement | null) => {
            ref(node);
            if (node) node.setAttribute("data-inview-ref", "");
          }}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="flex flex-col items-center"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-red-800 mb-2">
              Trusted by Leading Businesses
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
              Industry leaders who rely on our payment solutions
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-10 w-full max-w-4xl mx-auto"
          >
            {customerLogos.map((logo, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="flex items-center justify-center p-4 transition-all duration-300 rounded-lg"
              >
                <img
                  src={logo.src || "/placeholder.svg"}
                  alt={logo.alt}
                  className={`w-auto object-contain opacity-80 hover:opacity-100 transition-opacity ${
                    logo.size === "large" ? "h-6 md:h-8" : "h-5 md:h-6"
                  }`}
                  style={{
                    maxHeight: logo.size === "large" ? "32px" : "24px",
                  }}
                />
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={itemVariants} className="mt-10 text-center">
            <p className="text-gray-500 text-sm">
              Join thousands of businesses that trust VasPay for their payment
              needs
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="mt-4 px-6 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-blue-700 transition-colors"
              onClick={handleNavigate}
            >
              Become a Partner
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Customers;
